import { FormattedMessage, useIntl } from "react-intl";
import md5 from "md5";
import clsx from "clsx";
import MainView from "views/MainView";
import React, { useEffect, useState, useRef } from "react";
import { Spinner } from "components";
import {
  alexaAuth,
  googleAuth,
  queryStrToObject,
  isEmail,
  IAutLoginBody,
  publicPath,
} from "request";

const Login = () => {
  const { formatMessage } = useIntl();
  const [passwordError, setPasswordError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);

  const queryObject = useRef<any>(undefined);
  const redirectURI = useRef<string>("");

  const validatePasswords = () => {
    return email && email.trim() && password && password.trim() && !emailError;
  };

  const handleAuth = async (
    authFunction: (data: IAutLoginBody) => Promise<any>
  ) => {
    try {
      setIsLoading(true);
      const response = await authFunction({
        params: {
          password: md5(password),
          email,
          client_id: queryObject.current.client_id,
          redirect_uri: redirectURI.current,
          state: queryObject.current.state,
        },
      });

      setIsLoading(false);
      // const requestStatus = response?.status;
      // if (requestStatus !== 200) {
      //   const requestMessage = response?.message || "";
      //   if (requestMessage) {
      //     setPasswordError(requestMessage);
      //   }
      //   return;
      // }

      const code = response?.code;
      if (code === "200") {
        window.location.href = response?.result;
      } else if (code === "403") {
        setPasswordError("Parameter exception! Login failed");
      } else if (code === "405") {
        setPasswordError("Incorrect account or password");
      } else {
        setPasswordError(response?.msg);
      }
    } catch (error: any) {
      setIsLoading(false);
      const requestMessage = error?.message || "";
      if (requestMessage) {
        setPasswordError(requestMessage);
      }
    }
  };

  const onAlexaAuth = () => {
    handleAuth(alexaAuth);
  };

  const onGoogleAuth = () => {
    handleAuth(googleAuth);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || isLoading) return;

    const isAlexa = redirectURI.current.indexOf("amazon") > -1;

    if (isAlexa) {
      onAlexaAuth();
    } else {
      onGoogleAuth();
    }
  };

  const onblur = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value) value = value.trim();
    if (!value) return;
    if (!isEmail(email)) {
      setEmailError(formatMessage({ id: "login.form.email.error" }));
    }
  };

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    setPassword(value);
    if (emailError) {
      setPasswordError("");
    }
  };

  useEffect(() => {
    const queryData = queryStrToObject();
    if (queryData) {
      queryObject.current = queryData;
      redirectURI.current = decodeURIComponent(queryData?.redirect_uri);
    }
  }, []);

  useEffect(() => {
    setDisabled(!validatePasswords());
  }, [password, email, emailError]);

  return (
    <MainView backgroundClassName="bg-white dark:bg-black1b1f1e">
      <div className="flex items-center flex-col max-w-[650px] mx-auto">
        <img
          src={`${publicPath}/icons/ic_logo.png`}
          alt=""
          className="w-[54px] h-[54px] my-6"
        />
        <div className="font-bold text-lg px-10">
          {redirectURI.current.indexOf("amazon") > -1 ? (
            <FormattedMessage id="login.title.alexa" />
          ) : (
            <FormattedMessage id="login.title.google" />
          )}
        </div>
        <form className="w-full py-11 px-6" onSubmit={onSubmit}>
          <div
            className={clsx(
              "flex items-center border-b border-solid w-full relative",
              {
                "border-[#dcdcdc] dark:border-gray484848": !emailError,
                "border-error": emailError,
              }
            )}
          >
            <input
              type="text"
              name="email"
              value={email}
              onChange={onChangeEmail}
              onBlur={onblur}
              placeholder={formatMessage({
                id: "login.form.email.placeholder",
              })}
              className="flex-1 p-2 outline-none text-lg bg-transparent"
            />
            {emailError && (
              <p className="text-error mt-2 leading-5 absolute bottom-[-24px] left-0 text-sm">
                {emailError}
              </p>
            )}
          </div>
          <div
            className={clsx(
              "border-b border-solid w-full flex items-end mt-12 relative",
              {
                "border-[#dcdcdc] dark:border-gray484848": !passwordError,
                "border-error": passwordError,
              }
            )}
          >
            <input
              type={isShowPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={onChangePassword}
              placeholder={formatMessage({
                id: "login.form.pasowrd.placeholder",
              })}
              className="p-2 flex-1 outline-none text-lg bg-transparent"
            />
            <div
              className="w-5 min-h-5 pb-2 mr-2 cursor-pointer"
              onClick={() => setIsShowPassword((prev) => !prev)}
            >
              {isShowPassword ? (
                <img alt="" src={`${publicPath}/icons/ic_eye@2x.png`} />
              ) : (
                <img src={`${publicPath}/icons/ic_eye_hide@2x.png`} alt="" />
              )}
            </div>

            {passwordError && (
              <p className="text-error mt-2 leading-5 absolute bottom-[-24px] left-0 text-sm">
                {passwordError}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={disabled || isLoading}
            className="w-full h-[54px] bg-primary rounded-lg border border-sol border-primary text-white text-opacity-90 text-lg flex items-center justify-center mt-[75px] disabled:bg-[#cdcdcd] dark:disabled:bg-gray484848 disabled:border-[#dcdcdc] disabled:dark:border-gray484848 font-medium"
          >
            {isLoading ? (
              <Spinner extraSmall className={{ "border-gray666": true }} />
            ) : (
              <FormattedMessage id="login.form.button" />
            )}
          </button>
          <div className="text-[#999] dark:text-white60 mt-9">
            <FormattedMessage id="login.footer.text" />
          </div>
        </form>
      </div>
    </MainView>
  );
};

export default Login;
